<script setup lang="ts">
import { gsap } from 'gsap'

const root = ref<HTMLElement | null>(null)
const tourOpen = ref(false)

  const localePath = useLocalePath()

onMounted(() => {
  // The hero intro reveal is now CSS-driven (see <style>) so the above-the-fold
  // content paints immediately instead of waiting for the GSAP bundle to load
  // and execute — that JS-gated reveal (which hid the H1 and image until GSAP
  // ran) was the main thing delaying LCP. GSAP here only drives the decorative
  // ambient blob/image motion below.

  // Pin each blob to a screen edge as its resting position. Runs on every screen
  // so the gradient wash is composed correctly even where we don't animate.
  const blobEls = Array.from(root.value?.querySelectorAll<SVGElement>('.hero-blob') ?? [])
  const radii = blobEls.map(el => el.clientWidth / 2)
  let w = root.value?.clientWidth ?? window.innerWidth
  let h = root.value?.clientHeight ?? window.innerHeight
  // Spawn points pinned to the screen edges (left, right, bottom, top).
  const edges = [
    { x: 0, y: h * 0.5 },
    { x: w, y: h * 0.5 },
    { x: w * 0.5, y: h },
    { x: w * 0.5, y: 0 },
  ]
  blobEls.forEach((el, i) => {
    const e = edges[i % edges.length]
    gsap.set(el, { x: e.x - radii[i], y: e.y - radii[i] })
  })

  // Continuous ambient motion (image float + blob breathing/wander) is the
  // expensive bit: transform-animating three heavily-blurred 600px SVGs every
  // frame thrashes the mobile compositor and makes the hero text appear to
  // vibrate. Gate it to large screens with motion allowed; phones keep the blobs
  // static as a still gradient wash. matchMedia auto-cleans on unmount/teardown.
  const mm = gsap.matchMedia()
  mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
    // Continuous float on hero image
    gsap.to('.hero-image', {
      y: -16,
      duration: 3,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
      delay: 1.2,
    })

    // Breathing: scale + opacity drift together so each glow softly swells and
    // brightens. Desynced durations keep them from pulsing as one.
    gsap.to('.hero-blob-1', { scale: 1.12, opacity: 0.4, duration: 9, repeat: -1, yoyo: true, ease: 'sine.inOut' })
    gsap.to('.hero-blob-2', { scale: 1.14, opacity: 0.52, duration: 11, repeat: -1, yoyo: true, ease: 'sine.inOut', delay: -3 })
    gsap.to('.hero-blob-3', { scale: 1.1, opacity: 0.3, duration: 13, repeat: -1, yoyo: true, ease: 'sine.inOut', delay: -6 })

    // Each blob drifts from its edge to random points inside the hero forever —
    // picking a fresh random target (and duration) each time it arrives, so the
    // three wander independently and never repeat a path.
    const wander = (i: number) => {
      const r = radii[i]
      gsap.to(blobEls[i], {
        x: gsap.utils.random(Math.min(r, w - r), Math.max(r, w - r)) - r,
        y: gsap.utils.random(Math.min(r, h - r), Math.max(r, h - r)) - r,
        duration: gsap.utils.random(10, 16),
        ease: 'sine.inOut',
        onComplete: () => wander(i),
      })
    }
    blobEls.forEach((_, i) => wander(i))

    const onResize = () => {
      w = root.value?.clientWidth ?? w
      h = root.value?.clientHeight ?? h
    }
    window.addEventListener('resize', onResize, { passive: true })

    return () => {
      gsap.killTweensOf(blobEls)
      gsap.killTweensOf('.hero-image')
      window.removeEventListener('resize', onResize)
    }
  })

  onUnmounted(() => mm.revert())
})
</script>

<template>
  <section id="hero" ref="root" class="min-h-svh pt-20 md:pt-24 lg:pt-28 pb-12 md:pb-16 lg:pb-20 bg-white relative z-10 flex items-center">
    <!-- Ambient gradient blobs: heavy blur creates a soft aurora wash behind the dots. -->
    <svg class="hero-blob hero-blob-1 absolute top-0 left-0 w-[38rem] h-[38rem] opacity-30 blur-3xl pointer-events-none" viewBox="0 0 200 200" aria-hidden="true">
      <defs>
        <linearGradient id="heroBlobGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#5CC8FF" stop-opacity="0.7" />
          <stop offset="55%" stop-color="#2EACFB" stop-opacity="0.62" />
          <stop offset="100%" stop-color="#2E83FB" stop-opacity="0.55" />
        </linearGradient>
      </defs>
      <path d="M44.7,-69.8C58.4,-61.8,69.9,-49.5,76.7,-35C83.6,-20.5,85.7,-3.7,82.2,11.8C78.7,27.3,69.6,41.4,58,52.7C46.4,64,32.3,72.6,16.6,76.7C0.9,80.8,-16.4,80.5,-31.8,74.3C-47.2,68,-60.7,55.8,-69.4,41C-78.1,26.2,-82,8.8,-79.6,-7.4C-77.3,-23.6,-68.8,-38.6,-57.1,-48.1C-45.4,-57.7,-30.5,-61.8,-15.5,-66.3C-0.6,-70.8,14.4,-75.7,28.5,-77C42.7,-78.3,56,-77.8,44.7,-69.8Z" transform="translate(100 100)" fill="url(#heroBlobGrad1)" />
    </svg>

    <svg class="hero-blob hero-blob-2 absolute top-0 left-0 w-[34rem] h-[34rem] opacity-40 blur-3xl pointer-events-none" viewBox="0 0 200 200" aria-hidden="true">
      <defs>
        <linearGradient id="heroBlobGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#F23FB0" stop-opacity="0.7" />
          <stop offset="55%" stop-color="#CB2097" stop-opacity="0.6" />
          <stop offset="100%" stop-color="#8B2FC9" stop-opacity="0.5" />
        </linearGradient>
      </defs>
      <path d="M38.5,-64.7C51.1,-57.7,63.2,-49.1,70.8,-37.3C78.4,-25.5,81.5,-10.5,79.7,3.6C77.9,17.7,71.2,30.9,62.1,42.5C53,54.1,41.5,64.1,28.3,69.8C15.1,75.5,0.2,76.9,-14.8,74.3C-29.8,71.7,-44.9,65.1,-56.4,54.4C-67.9,43.7,-75.8,28.9,-78.5,13.1C-81.2,-2.7,-78.7,-19.5,-71.2,-33.4C-63.7,-47.3,-51.2,-58.3,-37.6,-65.1C-24,-71.9,-9.3,-74.5,2.4,-78.3C14.1,-82.1,25.9,-71.7,38.5,-64.7Z" transform="translate(100 100)" fill="url(#heroBlobGrad2)" />
    </svg>

    <svg class="hero-blob hero-blob-3 absolute top-0 left-0 w-[30rem] h-[30rem] opacity-20 blur-3xl pointer-events-none" viewBox="0 0 200 200" aria-hidden="true">
      <defs>
        <linearGradient id="heroBlobGrad3" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#6366F1" stop-opacity="0.65" />
          <stop offset="100%" stop-color="#2E83FB" stop-opacity="0.5" />
        </linearGradient>
      </defs>
      <path d="M44.7,-69.8C58.4,-61.8,69.9,-49.5,76.7,-35C83.6,-20.5,85.7,-3.7,82.2,11.8C78.7,27.3,69.6,41.4,58,52.7C46.4,64,32.3,72.6,16.6,76.7C0.9,80.8,-16.4,80.5,-31.8,74.3C-47.2,68,-60.7,55.8,-69.4,41C-78.1,26.2,-82,8.8,-79.6,-7.4C-77.3,-23.6,-68.8,-38.6,-57.1,-48.1C-45.4,-57.7,-30.5,-61.8,-15.5,-66.3C-0.6,-70.8,14.4,-75.7,28.5,-77C42.7,-78.3,56,-77.8,44.7,-69.8Z" transform="translate(100 100)" fill="url(#heroBlobGrad3)" />
    </svg>

    <!-- Dot grid background -->
    <div class="absolute inset-0 bg-[radial-gradient(circle,_#d1d5db_1.5px,_transparent_1.5px)] [background-size:28px_28px] opacity-50 pointer-events-none" aria-hidden="true"/>

    <div class="container relative grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <div class="text-center lg:text-start">
        <!-- badge -->
        <div class="hero-badge bfm-badge bg-creamy-yellow text-gray-900 px-3 md:px-4 py-1.5 md:py-2 text-sm md:text-base lg:text-lg mb-4 lg:mb-6">
          <UIcon name="i-approved-arrow-upscale" class="text-heart-purple size-4 md:size-5" />
          {{ $t('page.home.hero_new.badge') }}
        </div>

        <h1 class="hero-title text-[clamp(1.875rem,5vw,3.4rem)] font-bold leading-[1.1] rtl:leading-[1.3] text-deep-indigo mb-4 lg:mb-5">
          <span class="text-sky-blue">{{ $t('page.home.hero_new.title_1') }}</span>{{ $t('page.home.hero_new.title_2') }}<span class="text-heart-purple">{{ $t('page.home.hero_new.title_3') }}</span>{{ $t('page.home.hero_new.title_4') }}
        </h1>

        <p class="hero-subtitle text-base md:text-lg lg:text-xl font-medium text-overcast-night leading-relaxed max-w-sm md:max-w-lg lg:max-w-140 mx-auto lg:mx-0 mb-2 lg:mb-2">
          {{ $t('page.home.hero_new.subtitle') }}
        </p>
        <p class="hero-subtitle text-base md:text-lg lg:text-xl font-medium text-overcast-night leading-relaxed max-w-sm md:max-w-lg lg:max-w-140 mx-auto lg:mx-0 mb-8 lg:mb-10">
          {{ $t('page.home.hero_new.subtitle_2') }}
        </p>

        <div class="hero-cta flex items-center justify-center lg:justify-start gap-4 lg:gap-7 flex-wrap">
          <a
v-magnetic :href="localePath('/request-demo')"
            class="bfm-btn bfm-btn-md md:bfm-btn-lg bg-cheese-yellow py-2">
            {{ $t('page.home.hero_new.get_started') }}
          </a>
          <CommonWavyBorder :wl="20" :amp="6" :th="2" color="#FDC02D" :speed="0.8" :sides="['bottom']">
            <button type="button" class="px-2 pb-3 text-gray-900 font-bold transition-colors hover:text-sky-blue flex items-center gap-2" @click="tourOpen = true">
              <UIcon name="i-heroicons-play-circle-solid" class="text-heart-purple size-6" />
              {{ $t('page.home.hero_new.quick_tour') }}
            </button>
          </CommonWavyBorder>
          <LandingTourVideoModal v-model="tourOpen" />
        </div>
        <p class="hero-cta text-sm md:text-base font-bold text-gray-500 mt-5 text-center lg:text-start">
          {{ $t('page.home.hero_new.note') }}
        </p>
      </div>

      <div class="flex items-center justify-center">
        <img
          src="/hero.svg"
          alt="bookforme online booking app shown on a phone"
          width="519"
          height="528"
          fetchpriority="high"
          decoding="async"
          class="hero-image w-full max-w-70 sm:max-w-sm md:max-w-md lg:max-w-lg"
        >
      </div>
    </div>
  </section>
</template>

<style scoped>
/* CSS-driven hero intro. Runs on first paint with no JavaScript dependency, so
   the largest above-the-fold elements are painted immediately. The H1 and hero
   image animate transform ONLY (opacity stays 1) so they count toward LCP from
   the very first frame; the smaller badge / subtitle / CTA may fade in. */
@media (prefers-reduced-motion: no-preference) {
  .hero-badge { animation: heroFadeUp 0.6s ease both; }
  .hero-title { animation: heroRise 0.6s ease both 0.05s; }
  .hero-subtitle { animation: heroFadeUp 0.7s ease both 0.15s; }
  .hero-cta { animation: heroFadeUp 0.7s ease both 0.25s; }
  .hero-image { animation: heroRise 0.8s ease both 0.1s; }
}

@keyframes heroFadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: none; }
}

/* Transform-only: the element stays fully opaque (LCP-safe). */
@keyframes heroRise {
  from { transform: translateY(14px); }
  to { transform: none; }
}
</style>
